/**
 * DevicesPanel — Program 5 desktop identity/authority command center.
 *
 * Lists the registered desktop installs (exe devices) with observed status, and lets
 * an admin rename or retire them. This is the foundation the offline write-
 * authority ("primary") controls and credential state.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Monitor, RefreshCw, Trash2, Pencil, Check, X, AlertTriangle, Star, ShieldOff, Download } from 'lucide-react';
import { toast } from 'sonner';

import { useDeviceManagement } from '@/hooks/useDeviceManagement';
import { DESKTOP_INSTALLER_URL } from '@/config/constants';
import { formatDateTimeWithZone } from '@/utils/dateUtils';

const KILL_CONFIRM_WORD = 'REVOKE';
const AUTHORIZATION_LABELS = {
  active: null,
  pending: 'Registration pending',
  legacy: 'Offline authorization needs renewal',
};
const DevicesWorkspace = ({ model, onOpenSection }) => {
  const { controller, devices, timezone: shopTimezone } = model;
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [armedRetireId, setArmedRetireId] = useState(null);
  const [showKill, setShowKill] = useState(false);
  const [killText, setKillText] = useState('');
  const [feedback, setFeedback] = useState('');
  const editor = useRef({ id: null, revision: 0 });
  const kill = useRef({ open: false, revision: 0 });
  const arm = useRef(null);
  const mounted = useRef(false);
  const activity = useRef(0);
  const busy = model.pending || !model.writable;
  const owns = () => mounted.current && controller.owns();
  const findDevice = id => controller.getSnapshot().devices.find(device => device.install_id === id);
  const clearArm = () => {
    if (arm.current) clearTimeout(arm.current.timer);
    arm.current = null; setArmedRetireId(null);
  };
  useEffect(() => {
    mounted.current = true;
    const retire = () => {
      mounted.current = false;
      if (arm.current) clearTimeout(arm.current.timer);
      arm.current = null; editor.current = { id: null, revision: 0 }; kill.current = { open: false, revision: 0 }; ++activity.current;
    };
    return retire;
  }, []);
  const closeEdit = () => {
    ++activity.current; editor.current = { id: null, revision: 0 }; setEditingId(null); setEditName('');
  };
  const changeName = value => { ++editor.current.revision; setEditName(value); };
  const openKill = open => {
    if (!owns()) return;
    ++activity.current; clearArm(); kill.current = { open, revision: 0 };
    setShowKill(open); setKillText(''); if (open) setFeedback('');
  };
  const changeKillText = value => { ++kill.current.revision; setKillText(value); };
  const showResult = (result, accepted) => {
    if (result.kind === 'accepted') { setFeedback(accepted); toast.success(accepted); }
    else if (['unknown', 'rejected'].includes(result.kind)) setFeedback(result.message);
  };
  const load = () => { if (owns()) { clearArm(); return controller.refresh(); } };

  const fmtSeen = (iso) => formatDateTimeWithZone(iso, shopTimezone) || '—';
  const desktopAvailable = Boolean(window.lvbDesktop);

  // Which row is the computer the operator is sitting at. The desktop bridge
  // exposes a hostname and a primary flag but no install id (TCA-26100804), so
  // this matches on a hostname appearing exactly once and cross-checks the
  // primary flag. An ambiguous or contradictory match marks nothing: a missing
  // chip is harmless, a chip on the wrong machine is not.
  const selfInstallId = useMemo(() => {
    const host = window.lvbDesktop?.hostname;
    if (!host) return null;
    const matches = devices.filter((d) => d.hostname === host);
    if (matches.length !== 1) return null;
    const selfPrimary = window.lvbDesktop?.deviceStatus?.isPrimary;
    if (typeof selfPrimary === 'boolean'
      && selfPrimary !== Boolean(matches[0].is_primary)) return null;
    return matches[0].install_id;
  }, [devices]);

  const startEdit = (device) => {
    if (!owns()) return;
    ++activity.current; clearArm(); editor.current = { id: device.install_id, revision: 0 }; setFeedback('');
    setEditingId(device.install_id);
    setEditName(device.display_name || device.hostname || '');
  };

  const saveEdit = async (installId) => {
    if (!owns() || controller.blocked() || editor.current.id !== installId || !findDevice(installId)) return;
    const opening = editor.current; const revision = opening.revision;
    const name = editName.trim();
    if (!name) { closeEdit(); return; }
    clearArm(); setFeedback('');
    const result = await controller.mutate('rename', installId, name);
    if (!owns() || editor.current !== opening) return;
    showResult(result, opening.revision === revision ? 'Device renamed' : 'Submitted name saved. Later edits are still unsaved.');
    if (result.kind === 'accepted' && opening.revision === revision) closeEdit();
  };

  const handleRetire = async (installId) => {
    if (!owns() || controller.blocked() || !findDevice(installId)) return;
    const currentArm = arm.current;
    if (currentArm?.id !== installId || Date.now() >= currentArm.expires) {
      clearArm(); ++activity.current; setFeedback('');
      const opening = { id: installId, expires: Date.now() + 3000 };
      arm.current = opening;
      setArmedRetireId(installId);
      opening.timer = setTimeout(() => { if (owns() && arm.current === opening) clearArm(); }, 3000);
      return;
    }
    clearArm(); const context = ++activity.current;
    const result = await controller.mutate('retire', installId);
    if (owns() && context === activity.current) showResult(result, 'Device retired');
  };

  const handleAssign = async (device) => {
    if (!owns() || controller.blocked()) return;
    const context = activity.current;
    const eligible = () => {
      const current = findDevice(device.install_id);
      return current && current.online && current.authorization_status === 'active' && !current.is_primary
        && (current.display_name || current.hostname) === (device.display_name || device.hostname);
    };
    if (!eligible()) return;
    if (!window.confirm(
      `Make "${device.display_name || device.hostname}" the primary device? `
      + 'It will be the only install allowed to write offline (once that is enabled).',
    )) return;
    if (!owns() || context !== activity.current || controller.blocked() || !eligible()) return;
    clearArm(); setFeedback('');
    const result = await controller.mutate('assign', device.install_id);
    if (owns() && context === activity.current) showResult(result, 'Primary assigned');
  };

  const handleKill = async () => {
    if (!owns() || controller.blocked() || !kill.current.open || killText.trim() !== KILL_CONFIRM_WORD) return;
    const opening = kill.current; const revision = opening.revision;
    clearArm(); setFeedback('');
    const result = await controller.mutate('revoke');
    if (!owns() || kill.current !== opening) return;
    showResult(result, opening.revision === revision ? 'Primary revoked' : 'Submitted revocation accepted. Later confirmation text has not been submitted.');
    if (result.kind === 'accepted' && opening.revision === revision) openKill(false);
  };


  return (
    <div className="space-y-4" data-testid="devices-panel">
      {feedback && <p role="status">{feedback}</p>}
      {model.pending && <p role="status">A device change is still settling. Further changes are unavailable until it settles.</p>}
      {!model.writable && <p role="status">Device changes require an online administrator session.</p>}
      {model.local && <p role="status">This local view does not establish the current cloud device registry.</p>}
      {model.state === 'loading' && <p role="status">Loading…</p>}
      {model.state === 'unavailable' && <p role="alert">{model.known ? 'Showing last-known devices. The current registry could not be loaded.' : 'The device registry could not be loaded.'}</p>}
      {model.known && <p className="text-xs text-stone-500">Device status is from the last successful check.</p>}
      {editingId && !devices.some(device => device.install_id === editingId) && (
        <div>
          <p role="status">The edited device is no longer in this view. Your name draft is retained; refresh to review its status.</p>
          <Input aria-label="Retained device name" value={editName} onChange={event => changeName(event.target.value)} data-testid={`device-name-input-${editingId}`} />
          <button onClick={closeEdit}>Cancel rename</button>
        </div>
      )}
      <div className="flex items-center justify-between">
        <p className="text-sm text-stone-500">
          Desktop installs registered to this shop. A device registers itself the
          first time it runs online.
        </p>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            className="gap-1.5"
            data-testid="devices-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Browser only: in the desktop app the shop plainly already has it. */}
      {!desktopAvailable && (
      <div
        className="flex items-center justify-between gap-4 rounded-xl border border-stone-200 bg-stone-50 px-4 py-3"
        data-testid="devices-download"
      >
        <div className="min-w-0">
          <p className="text-sm font-semibold text-stone-800">Get the desktop app</p>
        </div>
        {DESKTOP_INSTALLER_URL ? (
          <a
            href={DESKTOP_INSTALLER_URL}
            download
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-medium text-white hover:bg-primary/90 transition-colors flex-shrink-0"
            data-testid="devices-download-btn"
          >
            <Download className="h-4 w-4" />
            Download
          </a>
        ) : (
          <span className="text-xs text-stone-400 flex-shrink-0" data-testid="devices-download-unavailable">
            Download not available yet
          </span>
        )}
      </div>
      )}

      {model.state === 'available' && devices.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-10 text-center border-2 border-dashed border-stone-200 rounded-xl">
          <Monitor className="h-8 w-8 text-stone-300" />
          <p className="text-sm font-medium text-stone-500">{model.local ? 'No devices in this local view' : 'No devices registered yet'}</p>
          {!model.local && <p className="text-xs text-stone-400">Open the desktop app online to register it here.</p>}
        </div>
      ) : (
        <div className="space-y-2">
          {devices.map((d) => (
            <div
              key={d.install_id}
              className="flex items-center gap-3 p-3 rounded-xl border border-stone-200 bg-white"
              data-testid={`device-row-${d.install_id}`}
            >
              <span
                className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${d.online ? 'bg-green-500' : 'bg-stone-300'}`}
                title={d.online ? 'Online' : 'Offline'}
                data-testid={`device-status-${d.install_id}`}
              />
              <div className="flex-1 min-w-0">
                {editingId === d.install_id ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={editName}
                      onChange={(e) => changeName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(d.install_id); if (e.key === 'Escape') closeEdit(); }}
                      autoFocus
                      className="h-8 text-sm"
                      data-testid={`device-name-input-${d.install_id}`}
                    />
                    <button disabled={busy} data-testid={`device-name-save-${d.install_id}`} onClick={() => saveEdit(d.install_id)} className="text-green-600 hover:text-green-700" title="Save">
                      <Check className="h-4 w-4" />
                    </button>
                    <button onClick={closeEdit} className="text-stone-400 hover:text-stone-600" title="Cancel">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-stone-800 truncate">
                      {d.display_name || d.hostname || d.install_id.slice(0, 8)}
                    </span>
                    <button
                      onClick={() => startEdit(d)}
                      className="text-stone-300 hover:text-stone-600 flex-shrink-0"
                      title="Rename"
                      data-testid={`device-rename-${d.install_id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
                <div className="text-xs text-stone-400 mt-0.5 truncate">
                  {d.online ? 'Online' : `Last seen ${fmtSeen(d.last_seen_at)}`}
                  {d.app_version && <span> · v{d.app_version}</span>}
                  {d.hostname && d.hostname !== d.display_name && <span> · {d.hostname}</span>}
                  {AUTHORIZATION_LABELS[d.authorization_status] && (
                    <span
                      className="text-amber-700"
                      data-testid={`device-authorization-${d.install_id}`}
                    >
                      {' · '}{AUTHORIZATION_LABELS[d.authorization_status]}
                    </span>
                  )}
                </div>
                {d.install_id === selfInstallId && onOpenSection && (
                  <button
                    type="button"
                    onClick={() => onOpenSection?.('this-device')}
                    className="mt-1 text-xs font-semibold text-primary hover:underline"
                    data-testid={`device-readiness-link-${d.install_id}`}
                  >
                    See this device’s offline readiness →
                  </button>
                )}
              </div>

              {d.install_id === selfInstallId && (
                <span
                  className="inline-flex items-center rounded-full bg-primary/10 text-primary px-2.5 py-1 text-xs font-semibold flex-shrink-0"
                  data-testid={`device-self-chip-${d.install_id}`}
                >
                  This Device
                </span>
              )}

              {d.is_primary ? (
                <>
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 px-2.5 py-1 text-xs font-semibold flex-shrink-0"
                    data-testid={`device-primary-badge-${d.install_id}`}
                  >
                    <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />
                    Primary
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openKill(true)}
                    className="flex-shrink-0 h-8 text-xs gap-1 text-red-600 hover:text-red-700"
                    data-testid="devices-revoke-primary"
                  >
                    <ShieldOff className="h-3.5 w-3.5" />
                    Revoke primary
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleAssign(d)}
                  disabled={busy || !d.online || d.authorization_status !== 'active'}
                  title={
                    d.authorization_status !== 'active'
                      ? 'Device must complete secure registration before becoming primary'
                      : (d.online
                        ? 'Make this the primary device'
                        : 'Device must be online to become primary')
                  }
                  className="flex-shrink-0 h-8 text-xs gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid={`device-make-primary-${d.install_id}`}
                >
                  <Star className="h-3.5 w-3.5" />
                  Make primary
                </Button>
              )}

              <Button
                variant={armedRetireId === d.install_id ? 'destructive' : 'ghost'}
                size="sm"
                onClick={() => handleRetire(d.install_id)}
                disabled={busy}
                className="flex-shrink-0 h-8 gap-1 text-xs"
                title={armedRetireId === d.install_id ? 'Click again to confirm' : 'Retire device'}
                data-testid={`device-retire-${d.install_id}`}
              >
                {armedRetireId === d.install_id ? (
                  <><AlertTriangle className="h-3.5 w-3.5" /> Sure?</>
                ) : (
                  <Trash2 className="h-3.5 w-3.5 text-red-500" />
                )}
              </Button>
            </div>
          ))}
        </div>
      )}

      {showKill && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          data-testid="kill-primary-modal"
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-stone-200">
              <ShieldOff className="h-4 w-4 text-red-600" />
              <span className="text-sm font-bold text-stone-800">Revoke primary from all devices</span>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-stone-600">
                This removes offline-write authority from every device. No device will be
                able to write offline until you assign a new primary. Type{' '}
                <span className="font-mono font-semibold text-stone-800">{KILL_CONFIRM_WORD}</span> to confirm.
              </p>
              <Input
                value={killText}
                onChange={(e) => changeKillText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleKill(); }}
                placeholder={KILL_CONFIRM_WORD}
                autoFocus
                data-testid="kill-primary-input"
              />
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-stone-200">
              <button
                type="button"
                onClick={() => openKill(false)}
                data-testid="kill-primary-cancel"
                className="text-sm font-semibold text-stone-500 hover:text-stone-800 px-3 py-2"
              >
                Cancel
              </button>
              <Button
                variant="destructive"
                onClick={handleKill}
                disabled={busy || killText.trim() !== KILL_CONFIRM_WORD}
                className="disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="kill-primary-confirm"
              >
                Revoke primary
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function DevicesPanel({ onOpenSection = null }) {
  const model = useDeviceManagement();
  if (!model.active) return <p role="status">{model.connectionError ? 'The backend connection changed. Reload before managing devices.' : 'Device management is unavailable for this session.'}</p>;
  return <DevicesWorkspace key={model.controller.key} model={model} onOpenSection={onOpenSection} />;
}
